import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Button } from "@legends/ui";
import {
  fetchAudit,
  fetchCaveLegendMappings,
  fetchMetrics,
  fetchNeverAiredBroadcasts,
  fetchRecordingStats,
  reclaimCaveCast,
  type AuditEntry,
  type CaveMapping,
} from "../api";
import { AsyncState, errMsg } from "../sections";
import { AUDIT_ACTION_LABEL, auditLine } from "./auditLabels";

// The admin home (ADR 0160): lead with what's blocking a cave from going on air,
// not eleven flat tables. One row per blocker with the fix inline, then the most
// recent back-office activity. Data-backed and honest — a category with no rows
// disappears rather than showing a placeholder.

type Severity = "block" | "warn";

interface Attention {
  key: string;
  severity: Severity;
  title: string;
  detail: string;
  action?: { label: string; onClick: () => void };
}

const cardStyle: CSSProperties = {
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-lg)",
  padding: "20px 22px",
};

const sevColor: Record<Severity, { dot: string; tint: string; edge: string }> = {
  block: { dot: "var(--live)", tint: "var(--live-tint)", edge: "var(--live-edge)" },
  warn: { dot: "var(--warn)", tint: "var(--warn-tint)", edge: "var(--warn-edge)" },
};

function caveSlugOf(host: string, id: string): string {
  return (host ?? "").split(".")[0].trim() || id;
}

/** Blockers derived from the cave↔legend↔user chain (the dissolved Mapping view). */
function mapMisconfigs(caves: CaveMapping[], openCave: (slug: string) => void): Attention[] {
  const out: Attention[] = [];
  for (const c of caves) {
    if (c.archived) continue;
    const slug = caveSlugOf(c.host, c.id);
    if (!c.legendId) {
      out.push({
        key: `cave-nolegend-${c.id}`,
        severity: "block",
        title: c.name,
        detail: "No legend is headlining this cave — it can't go on air.",
        action: { label: "Fix", onClick: () => openCave(slug) },
      });
    } else if (!c.legendUserId) {
      out.push({
        key: `cave-nouser-${c.id}`,
        severity: "block",
        title: c.name,
        detail: `${c.legendName ?? "The legend"} has no linked account — the legend can't sign in to go live.`,
        action: { label: "Fix", onClick: () => openCave(slug) },
      });
    }
  }
  return out;
}

function AttentionRow({ a }: { a: Attention }) {
  const s = sevColor[a.severity];
  return (
    <li
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "12px 14px",
        borderRadius: "var(--radius-md)",
        background: s.tint,
        border: `1px solid ${s.edge}`,
      }}
    >
      <span aria-hidden style={{ width: 8, height: 8, borderRadius: 999, background: s.dot, flex: "0 0 auto" }} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: "var(--fs-md)", color: "var(--text-1)", fontWeight: 600 }}>{a.title}</div>
        <div style={{ fontSize: "var(--fs-sm)", color: "var(--text-2)" }}>{a.detail}</div>
      </div>
      {a.action && (
        <Button size="sm" variant="accent" onClick={a.action.onClick} style={{ flex: "0 0 auto" }}>
          {a.action.label}
        </Button>
      )}
    </li>
  );
}

export default function AdminOverviewScreen({
  onOpenCave,
  onOpenAudit,
}: {
  onOpenCave: (slug: string) => void;
  onOpenAudit: () => void;
}) {
  const [attention, setAttention] = useState<Attention[] | null>(null);
  const [attnError, setAttnError] = useState(false);
  const [audit, setAudit] = useState<AuditEntry[] | null>(null);
  const [auditError, setAuditError] = useState(false);
  // False-start recovery (LSC-641): the inline "Reset" action's result, and a tick that
  // re-runs the queue load after a reclaim so the resolved row disappears.
  const [reclaimMsg, setReclaimMsg] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  // Reset a false-started CaveCast straight from the attention card (the panic button).
  // Stable (no deps) so it doesn't re-create `load`; a success bumps `tick` to refresh.
  const reclaimNeverAired = useCallback((caveId: string, eventId: string) => {
    setReclaimMsg(null);
    reclaimCaveCast(caveId, eventId)
      .then(() => {
        setReclaimMsg("Reset to not-started — the CaveCast is no longer showing as Live Now.");
        setTick((t) => t + 1);
      })
      .catch((err: unknown) => setReclaimMsg(errMsg(err)));
  }, []);

  const load = useCallback(() => {
    setAttnError(false);
    setAttention(null);
    // Four cheap aggregate reads assemble the queue; a partial failure still
    // shows what resolved (each category is independent).
    Promise.allSettled([
      fetchCaveLegendMappings(),
      fetchMetrics(),
      fetchRecordingStats(),
      fetchNeverAiredBroadcasts(),
    ]).then(([mapsR, metricsR, statsR, neverAiredR]) => {
      const rows: Attention[] = [];
      let anyOk = false;
        // False-starts first — a fan-visible "Live Now" that never aired is the most
        // urgent (it also fired a go-live push to ticket holders).
        if (neverAiredR.status === "fulfilled") {
          anyOk = true;
          for (const nb of neverAiredR.value) {
            rows.push({
              key: `never-aired-${nb.broadcastId}`,
              severity: "block",
              title: nb.caveName || nb.caveId,
              detail: `${nb.eventTitle || "A CaveCast"} is live but never went on air — fans see it as "Live Now". Reset it to not-started.`,
              action: { label: "Reset", onClick: () => reclaimNeverAired(nb.caveId, nb.eventId) },
            });
          }
        }
        if (mapsR.status === "fulfilled") {
          anyOk = true;
          rows.push(...mapMisconfigs(mapsR.value.caves, onOpenCave));
        }
        if (statsR.status === "fulfilled") {
          anyOk = true;
          const failed = statsR.value.failed ?? 0;
          if (failed > 0) {
            rows.push({
              key: "rec-failed",
              severity: "warn",
              title: `${failed} recording${failed === 1 ? "" : "s"} failed to render`,
              detail: "Open the cave's Recordings tab to review the reason and re-arm.",
            });
          }
        }
        if (metricsR.status === "fulfilled") {
          anyOk = true;
          const pending = metricsR.value.aggregate.pendingCheckouts;
          if (pending > 0) {
            rows.push({
              key: "checkouts-abandoned",
              severity: "warn",
              title: `${pending} checkout${pending === 1 ? "" : "s"} started but not completed`,
              detail: "Fans who began a purchase and didn't finish — no ticket was granted and no card was charged.",
            });
          }
        }
        if (!anyOk) {
          setAttnError(true);
          setAttention(null);
        } else {
          setAttention(rows);
        }
      },
    );
  }, [onOpenCave, reclaimNeverAired]);

  const loadAudit = useCallback(() => {
    setAuditError(false);
    fetchAudit({ limit: 6 })
      .then(setAudit)
      .catch(() => {
        setAudit(null);
        setAuditError(true);
      });
  }, []);

  useEffect(() => {
    load();
    loadAudit();
  }, [load, loadAudit, tick]);

  return (
    <div style={{ display: "grid", gap: 20, maxWidth: 760 }}>
      <section style={cardStyle} aria-label="Needs attention">
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: "var(--fs-title)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "var(--ls-display)",
              color: "var(--text-1)",
            }}
          >
            Needs attention
          </h2>
          <Button size="sm" variant="muted" onClick={load}>
            Refresh
          </Button>
        </div>
        {reclaimMsg && (
          <p style={{ margin: "10px 0 0", fontSize: "var(--fs-sm)", color: "var(--text-2)" }} role="status">
            {reclaimMsg}
          </p>
        )}
        <div style={{ marginTop: 14 }}>
          {attnError ? (
            <AsyncState status="error" errorText="Couldn't load the overview." onRetry={load} />
          ) : attention === null ? (
            <AsyncState status="loading" />
          ) : attention.length === 0 ? (
            <p style={{ margin: 0, fontSize: "var(--fs-md)", color: "var(--ok)" }}>
              All clear — every cave is live-ready, no failed recordings, no abandoned checkouts.
            </p>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
              {attention.map((a) => (
                <AttentionRow key={a.key} a={a} />
              ))}
            </ul>
          )}
        </div>
      </section>

      <section style={cardStyle} aria-label="Recent activity">
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: "var(--fs-title)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "var(--ls-display)",
              color: "var(--text-1)",
            }}
          >
            Recent activity
          </h2>
          <Button size="sm" variant="muted" onClick={onOpenAudit}>
            View all
          </Button>
        </div>
        <div style={{ marginTop: 14 }}>
          {auditError ? (
            <AsyncState status="error" errorText="Couldn't load recent activity." onRetry={loadAudit} />
          ) : audit === null ? (
            <AsyncState status="loading" />
          ) : audit.length === 0 ? (
            <AsyncState status="empty" emptyText="No back-office activity recorded yet." />
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
              {audit.map((e) => (
                <AuditRowCompact key={e.id} e={e} />
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

function AuditRowCompact({ e }: { e: AuditEntry }): ReactNode {
  return (
    <li style={{ display: "flex", alignItems: "baseline", gap: 10, fontSize: "var(--fs-sm)" }}>
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "var(--fs-2xs)",
          color: "var(--text-4)",
          whiteSpace: "nowrap",
        }}
      >
        {new Date(e.at).toLocaleString()}
      </span>
      <span style={{ color: "var(--text-2)" }}>{auditLine(e)}</span>
      <span
        style={{
          marginLeft: "auto",
          fontFamily: "var(--font-mono)",
          fontSize: "var(--fs-3xs)",
          textTransform: "uppercase",
          letterSpacing: "var(--ls-label)",
          color: "var(--text-4)",
          whiteSpace: "nowrap",
        }}
      >
        {AUDIT_ACTION_LABEL[e.action] ?? e.action}
      </span>
    </li>
  );
}
